/** @format */

/*****************************************************************************************************************************
 * Responsibility- Navigation for the account page for mobile and desktop. The navigation is agnostic to its page positioning.
 * The MainAccountLayout is responsible for placing the navigation and the view component in the corresponding position
 * in the page.
 * ************
 */

import {AccountMainLayout} from '../account_main_layout/account_main_layout';
import {Outlet, useNavigate, useLocation, useParams} from 'react-router-dom';
import {NavLink, withRouter, RouterContext} from 'frontier-utilities';
import React, {useState, useEffect, useContext} from 'react';
import {ConditionalButton} from '../../../../utilities';
import {Select} from '@sttark/design-system';
import SuperAgent from 'superagent';

// styles
import './nav.scss';

const useFetch = url => {
    const [data, updateData] = useState({messageCount: 0});

    useEffect(() => {
        async function fetchData() {
            const response = await SuperAgent.get(url).set('Accept', 'application/json');
            updateData(response.body);
        }
        fetchData();
    }, [url]);

    return data;
};

export default withRouter(props => {
    const accountCompletionStatus = useFetch('/account/users/account-completion-status');
    const unpaidCount = useFetch('/account/users/unpaid-count');

    const routerCtx = useContext(RouterContext);

    if (routerCtx.location.pathname === '/account/users/login') {
        // We do not want the menu for the login page
        return null;
    }

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const isOrdersActive =
        (location.pathname.includes('/account/orders') &&
            !location.pathname.includes('/account/orders/saved')) ||
        location.pathname === '/account/orders/index';

    const isInvoicesActive = location.pathname.includes('/account/charges');
    const isSavedOrdersActive = location.pathname.includes('/account/orders/saved');
    const isCompanyActive = location.pathname.includes('/account/companies');
    const isPreferencesActive = location.pathname.includes('/account/users/addresses');
    const isAssessmentsActive = location.pathname.includes(
        '/account/users/account-details/assessments'
    );

    const navItems = [
        {
            className: isOrdersActive
                ? 'active nav-link reorders-nav-link'
                : 'nav-link reorders-nav-link',
            link: '/account/orders/index',
            icon: 'fa fa-truck',
            title: 'Orders',
            id: 1
        },
        {
            badge: unpaidCount.unpaidCount,
            link: '/account/charges/index',
            className: isInvoicesActive ? 'active nav-link' : 'nav-link',
            icon: 'fa fa-money-bills',
            title: 'Invoices',
            id: 2
        },
        {
            className: isSavedOrdersActive
                ? 'active nav-link reorders-nav-link'
                : 'nav-link reorders-nav-link',
            link: '/account/orders/saved',
            title: 'Saved Quotes',
            icon: 'fa fa-save',
            id: 3
        },
        {
            link: '/account/companies/index',
            icon: 'fa fa-users-line',
            className: isCompanyActive ? 'active nav-link' : 'nav-link',
            title: 'Company',
            id: 4
        },
        {
            link: '/account/users/account-details/contact',
            icon: 'fa fa-gear',
            className: isPreferencesActive ? 'active nav-link' : 'nav-link',
            title: 'Preferences',
            id: 5,
            children: [
                // Assessments removed - now admin only feature
            ]
        },

        {
            link: '/account/users/logout',
            icon: 'fa fa-sign-out',
            className: 'nav-link',
            title: 'Logout',
            id: 9
        }
    ];

    let currentLocation = navItems.find(item => item.link === location.pathname);

    // dynamic routes will not match the absolute paths. We need to find the closest match
    if (!currentLocation) {
        const secondaryPath = navItems.find(item =>
            location.pathname.startsWith(item.link.split('/').slice(0, 1).join('/'))
        );
        const {id, orderId} = params;

        if (secondaryPath) {
            currentLocation = {
                icon: secondaryPath.icon,
                title:
                    secondaryPath.title +
                    ` ${id || orderId ? '/ ' : ''}${id ? id : orderId ? orderId : ''}`
            };
        }
    }

    return (
        <AccountMainLayout>
            <div className="navigation-02sk">
                <h4 className={'navigation-02sk__title'}>Account</h4>

                {/* Mobile view */}
                <div className="navigation-02sk__nav navigation-02sk__nav--mobile">
                    <h5>Nav</h5>
                    <Select
                        options={navItems.map((item, index) => ({
                            label: (
                                <span>
                                    <i className={`${item.icon} me-3`} />
                                    <b>{item.title}</b>
                                </span>
                            ),
                            value: item.link
                        }))}
                        initialValue={
                            <span className={'color-accent-primary'}>
                                <i className={`${currentLocation?.icon} me-3`} />
                                <b>{currentLocation?.title}</b>
                            </span>
                        }
                        className="navigation-nav-02sk__select"
                        onChange={url => navigate(url)}
                        placeholder=""
                        label=""
                    />
                </div>

                {/* Desktop view */}
                <nav className="navigation-02sk__nav navigation-02sk__nav--desktop">
                    <div className="navigation-nav-02sk__item">
                        {navItems.map((item, index) => {
                            return (
                                <a
                                    key={index}
                                    href={item.link}
                                    className={`d-flex align-items-center justify-content-start ${item.className}`}>
                                    <ConditionalButton enabled={!!item.badge}>
                                        <b className="item-02sk__badge d-inline-flex align-items-center justify-content-center">
                                            {item.badge}
                                        </b>
                                        <></>
                                    </ConditionalButton>
                                    <i className={`item-02sk__icon ${item.icon}`} />
                                    <span className={'item-02sk__title'}>{item.title}</span>
                                </a>
                            );
                        })}
                    </div>
                </nav>
            </div>

            {/* view component */}
            <Outlet />
        </AccountMainLayout>
    );
});
